// CrmType type and metadata for all CRM types
export type CrmType =
  | 'fub'
  | 'sierra'
  | 'kvcore'
  | 'lofty'
  | 'salesforce'
  | 'hubspot'
  | 'moxiworks'
  | 'test_crm';

// Shared field types for CRM forms
export type SharedFieldType =
  | 'string'
  | 'email'
  | 'password'
  | 'apiKey'
  | 'timezone'
  | 'dropdown';

// Define shared field configs in a central map
export const sharedFields = {
  clientName: {
    name: 'friendlyName',
    label: 'Client Name',
    required: true,
    type: 'string',
  },
  apiKey: { name: 'apiKey', label: 'API Key', required: true, type: 'apiKey' },
  timezone: {
    name: 'timezone',
    label: 'Timezone',
    required: true,
    type: 'dropdown',
    options: [
      { value: 'America/New_York', label: 'America/New_York' },
      { value: 'America/Chicago', label: 'America/Chicago' },
      { value: 'America/Denver', label: 'America/Denver' },
      { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
      { value: 'America/Anchorage', label: 'America/Anchorage' },
      { value: 'America/Phoenix', label: 'America/Phoenix' },
      { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu' },
    ],
  },
  email: { name: 'email', label: 'Email', required: false, type: 'email' },
  username: {
    name: 'username',
    label: 'Account Username',
    required: false,
    type: 'string',
  },
  accountPassword: {
    name: 'accountPassword',
    label: 'Account Password',
    required: false,
    type: 'string',
  },
  totpSecret: {
    name: 'totpSecret',
    label: 'Authenticator setup key',
    required: false,
    type: 'password',
  },
  recoveryCode: {
    name: 'recoveryCode',
    label: 'Recovery code',
    required: false,
    type: 'password',
  },
  authCookie: {
    name: 'authCookie',
    label: 'Web session cookies (JSON)',
    required: false,
    type: 'password',
  },
  requiredUsername: {
    name: 'username',
    label: 'Account Username',
    required: true,
    type: 'string',
  },
  requiredAccountPassword: {
    name: 'accountPassword',
    label: 'Account Password',
    required: true,
    type: 'string',
  },
  siteName: {
    name: 'siteName',
    label: 'Site Name',
    required: true,
    type: 'string',
  },
  subdomain: {
    name: 'subdomain',
    label: 'Subdomain',
    required: true,
    type: 'string',
  },
  hubspotDealId: {
    name: 'hubspotDealId',
    label: 'HubSpot Deal ID',
    required: true,
    type: 'string',
  },
  hubspotCompanyId: {
    name: 'hubspotCompanyId',
    label: 'HubSpot ID',
    required: true,
    type: 'string',
  },
  clientWebsite: {
    name: 'clientWebsite',
    label: 'Client Website',
    required: false,
    type: 'string',
  },
};

export type SharedFieldKey = keyof typeof sharedFields;

export interface CrmTypeMeta {
  label: string;
  supportsOnboarding: boolean;
  supportsSingleAgent: boolean;
  clientFields: SharedFieldKey[];
  singleAgentFields: SharedFieldKey[];
  /** Fields editable via the CRM credential editor. Omit for CRMs that don't support credential editing. */
  credentialFields?: SharedFieldKey[];
  fieldSuffix: Record<string, string>;
  fieldDefaults: Record<string, string>;
}

export const crmTypeMeta: Record<CrmType, CrmTypeMeta> = {
  fub: {
    label: 'Follow Up Boss',
    supportsOnboarding: true,
    supportsSingleAgent: true,
    clientFields: [
      'clientName',
      'timezone',
      'apiKey',
      'accountPassword',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    singleAgentFields: [
      'apiKey',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    credentialFields: [
      'apiKey',
      'username',
      'accountPassword',
      'totpSecret',
      'recoveryCode',
      'authCookie',
    ],
    fieldSuffix: {},
    fieldDefaults: {},
  },
  sierra: {
    label: 'Sierra',
    supportsOnboarding: true,
    supportsSingleAgent: false,
    clientFields: [
      'clientName',
      'timezone',
      'apiKey',
      'requiredUsername',
      'requiredAccountPassword',
      'siteName',
      'subdomain',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    singleAgentFields: [],
    credentialFields: ['apiKey', 'siteName', 'username', 'accountPassword'],
    fieldSuffix: {
      subdomain: '.sierrainteractivedev.com',
    },
    fieldDefaults: {},
  },
  kvcore: {
    label: 'BoldTrail',
    supportsOnboarding: true,
    supportsSingleAgent: true,
    clientFields: [
      'clientName',
      'timezone',
      'apiKey',
      'email',
      'accountPassword',
      'subdomain',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    singleAgentFields: [
      'apiKey',
      'subdomain',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    credentialFields: ['apiKey', 'email', 'accountPassword'],
    fieldSuffix: {
      subdomain: '.boldtrail.com',
    },
    fieldDefaults: {
      subdomain: 'app',
    },
  },
  lofty: {
    label: 'Lofty',
    supportsOnboarding: true,
    supportsSingleAgent: true,
    clientFields: [
      'clientName',
      'timezone',
      'email',
      'accountPassword',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    singleAgentFields: [
      'clientName',
      'timezone',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    fieldSuffix: {},
    fieldDefaults: {},
  },
  salesforce: {
    label: 'Salesforce',
    supportsOnboarding: false,
    supportsSingleAgent: false,
    clientFields: [
      'clientName',
      'requiredUsername',
      'requiredAccountPassword',
      'apiKey',
      'subdomain',
    ],
    singleAgentFields: [],
    credentialFields: ['username', 'accountPassword', 'apiKey', 'subdomain'],
    fieldSuffix: {
      subdomain: '.lightning.force.com',
    },
    fieldDefaults: {},
  },
  hubspot: {
    label: 'HubSpot',
    supportsOnboarding: false,
    supportsSingleAgent: false,
    clientFields: [],
    singleAgentFields: [],
    credentialFields: ['apiKey'],
    fieldSuffix: {},
    fieldDefaults: {},
  },
  moxiworks: {
    label: 'MoxiWorks',
    supportsOnboarding: true,
    supportsSingleAgent: true,
    clientFields: [
      'clientName',
      'timezone',
      'username',
      'accountPassword',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    singleAgentFields: [
      'clientName',
      'timezone',
      'username',
      'accountPassword',
      'hubspotDealId',
      'hubspotCompanyId',
      'clientWebsite',
    ],
    fieldSuffix: {},
    fieldDefaults: {},
  },
  test_crm: {
    label: 'Test CRM',
    supportsOnboarding: false,
    supportsSingleAgent: false,
    clientFields: [],
    singleAgentFields: [],
    fieldSuffix: {},
    fieldDefaults: {},
  },
};

export const getCrmDisplayName = (type?: CrmType): string => {
  if (!type) {
    return 'CRM';
  }
  return (
    crmTypeMeta[type]?.label || type.charAt(0).toUpperCase() + type.slice(1)
  );
};
